import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ContactFormProps, ComplaintFormData } from '../types';
import { RECAPTCHA_CONFIG, VALIDATION_RULES } from '../constants';
import '../styles/ContactForm.css';
import { useContactForm, useRecaptcha } from '../hooks';
import { submitContactForm } from '../utils';
import FilePicker from './FilePicker';
import FormField from './FormField';
import TextareaField from './TextareaField';
import DateField from './DateField';
import SelectField from './SelectField';
import FormRow from './FormRow';
import SubmitButton from './SubmitButton';
import StatusMessage from './StatusMessage';

const ContactForm: React.FC<ContactFormProps> = ({ 
  onSubmit = submitContactForm,
  className = '' 
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source') || undefined;
  const warrantyEmail = searchParams.get('warrantyEmail') || 'warranty@dreamfiregrill.com';
  const infoEmail = searchParams.get('infoEmail') || 'info@dreamfiregrill.com';
  const { formState, updateField, handleSubmit } = useContactForm();
  const { executeRecaptchaAction, isRecaptchaReady } = useRecaptcha({
    siteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '',
    action: RECAPTCHA_CONFIG.actions.CONTACT_FORM
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isRecaptchaReady()) {
      return;
    }

    try {
      const token = await executeRecaptchaAction();
      await handleSubmit(onSubmit, token, sourceParam);
    } catch (error) {
      // Error handling is done in the handleSubmit function
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateField(name as keyof ComplaintFormData, value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    updateField(name as keyof ComplaintFormData, files);
  };

  const handleRemoveFile = (fieldName: keyof ComplaintFormData, indexToRemove: number) => {
    const currentFiles = formState.data[fieldName];
    
    // Type guard: ensure we're working with FileList
    if (!currentFiles || !(currentFiles instanceof FileList) || currentFiles.length === 0) {
      return;
    }

    // Create a new FileList without the removed file using DataTransfer
    const dataTransfer = new DataTransfer();
    const fileArray = Array.from(currentFiles);
    
    fileArray.forEach((file, index) => {
      if (index !== indexToRemove) {
        dataTransfer.items.add(file);
      }
    });

    const newFileList = dataTransfer.files;
    
    // Update the field with the new FileList (or null if empty)
    updateField(fieldName, newFileList.length > 0 ? newFileList : null);
  };

  return (
    <div className={`contact-form ${className}`}>
      <form id="main-form" onSubmit={handleFormSubmit} noValidate>
        <FormRow>
          <FormField
            id="firstName"
            name="firstName"
            label={t('form.fields.firstName.label')}
            value={formState.data.firstName}
            error={formState.errors.firstName}
            required
            placeholder={t('form.fields.firstName.placeholder')}
            maxLength={VALIDATION_RULES.name.maxLength}
            onChange={handleInputChange}
          />

          <FormField
            id="lastName"
            name="lastName"
            label={t('form.fields.lastName.label')}
            value={formState.data.lastName}
            error={formState.errors.lastName}
            required
            placeholder={t('form.fields.lastName.placeholder')}
            maxLength={VALIDATION_RULES.name.maxLength}
            onChange={handleInputChange}
          />
        </FormRow>

        {/* Address Section */}
        <div className="address-section">
          <label className="section-label">
            {t('form.fields.streetAddress.label')} <span aria-label={t('accessibility.required')}>*</span>
          </label>
          
          <FormField
            id="streetAddress"
            name="streetAddress"
            label={t('form.fields.street.label')}
            value={formState.data.streetAddress}
            error={formState.errors.streetAddress}
            required
            placeholder={t('form.fields.street.placeholder')}
            labelPosition="below"
            onChange={handleInputChange}
          />

          <FormField
            id="addressLine2"
            name="addressLine2"
            label={t('form.fields.addressLine2.label')}
            value={formState.data.addressLine2}
            error={formState.errors.addressLine2}
            placeholder={t('form.fields.addressLine2.placeholder')}
            labelPosition="below"
            onChange={handleInputChange}
          />
        </div>

        <FormRow columns={3}>
          <FormField
            id="city"
            name="city"
            label={t('form.fields.city.label')}
            value={formState.data.city}
            error={formState.errors.city}
            required
            placeholder={t('form.fields.city.placeholder')}
            labelPosition="below"
            onChange={handleInputChange}
          />

          <FormField
            id="stateProvince"
            name="stateProvince"
            label={t('form.fields.stateProvince.label')}
            value={formState.data.stateProvince}
            error={formState.errors.stateProvince}
            placeholder={t('form.fields.stateProvince.placeholder')}
            labelPosition="below"
            onChange={handleInputChange}
          />

          <FormField
            id="zipPostal"
            name="zipPostal"
            label={t('form.fields.zipPostal.label')}
            value={formState.data.zipPostal}
            error={formState.errors.zipPostal}
            required
            placeholder={t('form.fields.zipPostal.placeholder')}
            labelPosition="below"
            onChange={handleInputChange}
          />
        </FormRow>

        <SelectField
          id="country"
          name="country"
          label={t('form.fields.country.label')}
          value={formState.data.country}
          error={formState.errors.country}
          required
          placeholder={t('form.fields.country.placeholder')}
          onChange={handleInputChange}
        />

        <FormField
          id="phone"
          name="phone"
          type="tel"
          label={t('form.fields.phone.label')}
          value={formState.data.phone}
          error={formState.errors.phone}
          required
          placeholder={t('form.fields.phone.placeholder')}
          onChange={handleInputChange}
        />

        <FormField
          id="email"
          name="email"
          type="email"
          label={t('form.fields.email.label')}
          value={formState.data.email}
          error={formState.errors.email}
          required
          placeholder={t('form.fields.email.placeholder')}
          onChange={handleInputChange}
        />

        <FormField
          id="productName"
          name="productName"
          label={t('form.fields.productName.label')}
          value={formState.data.productName}
          error={formState.errors.productName}
          required
          placeholder={t('form.fields.productName.placeholder')}
          onChange={handleInputChange}
        />

        <FormField
          id="color"
          name="color"
          label={t('form.fields.color.label')}
          value={formState.data.color}
          error={formState.errors.color}
          placeholder={t('form.fields.color.placeholder')}
          onChange={handleInputChange}
        />

        <FormField
          id="serialNumber"
          name="serialNumber"
          label={t('form.fields.serialNumber.label')}
          value={formState.data.serialNumber}
          error={formState.errors.serialNumber}
          placeholder={t('form.fields.serialNumber.placeholder')}
          onChange={handleInputChange}
        />

        <TextareaField
          id="issueDescription"
          name="issueDescription"
          label={t('form.fields.issueDescription.label')}
          value={formState.data.issueDescription}
          error={formState.errors.issueDescription}
          required
          placeholder={t('form.fields.issueDescription.placeholder')}
          onChange={handleInputChange}
        />

        <DateField
          id="issueDate"
          name="issueDate"
          label={t('form.fields.issueDate.label')}
          value={formState.data.issueDate}
          error={formState.errors.issueDate}
          required
          min={VALIDATION_RULES.date.min}
          onChange={handleInputChange}
        />

        <FormField
          id="retailer"
          name="retailer"
          label={t('form.fields.retailer.label')}
          value={formState.data.retailer}
          error={formState.errors.retailer}
          required
          placeholder={t('form.fields.retailer.placeholder')}
          onChange={handleInputChange}
        />

        <DateField
          id="purchaseDate"
          name="purchaseDate"
          label={t('form.fields.purchaseDate.label')}
          value={formState.data.purchaseDate}
          error={formState.errors.purchaseDate}
          required
          min={VALIDATION_RULES.date.min}
          onChange={handleInputChange}
        />

        <FilePicker
          id="issuePhotos"
          name="issuePhotos"
          label={t('form.fields.issuePhotos.label')}
          required
          multiple
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          value={formState.data.issuePhotos}
          error={formState.errors.issuePhotos}
          onChange={handleFileChange}
          onRemove={(index) => handleRemoveFile('issuePhotos', index)}
          helpText={t('form.fields.issuePhotos.helpText', { warrantyEmail })}
          maxFiles={10}
          maxTotalSize={50 * 1024 * 1024}
        />

        <FilePicker
          id="productPhotos"
          name="productPhotos"
          label={t('form.fields.productPhotos.label')}
          required
          multiple
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          value={formState.data.productPhotos}
          error={formState.errors.productPhotos}
          onChange={handleFileChange}
          onRemove={(index) => handleRemoveFile('productPhotos', index)}
          helpText={t('form.fields.productPhotos.helpText', { warrantyEmail })}
          maxFiles={10}
          maxTotalSize={50 * 1024 * 1024}
        />

        <FilePicker
          id="receipt"
          name="receipt"
          label={t('form.fields.receipt.label')}
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          value={formState.data.receipt}
          error={formState.errors.receipt}
          onChange={handleFileChange}
          onRemove={(index) => handleRemoveFile('receipt', index)}
          helpText={t('form.fields.receipt.helpText', { warrantyEmail, infoEmail })}
          maxFiles={5}
          maxTotalSize={10 * 1024 * 1024}
        />

        <SubmitButton
          isSubmitting={formState.isSubmitting}
          disabled={!isRecaptchaReady()}
        />
      </form>

      {formState.submitStatus && (
        <StatusMessage
          id="submit-status"
          message={formState.submitStatus}
          type={formState.submitStatusType === 'error' ? 'error' : 'success'}
        />
      )}

      {formState.errors.general && (
        <StatusMessage
          message={formState.errors.general}
          type="error"
        />
      )}
    </div>
  );
};

export default ContactForm;
